import { useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Filter, IQuestion, QuestionType } from 'cat-shared/utils'
import { AppSegment } from 'client/ui-building-blocks'
import { CreateQuestionFormValues } from 'client/utils'
import { QuestionModal } from 'client/components'
import { useCreateQuestion } from 'client/api'

import {
  useAppContext,
  usePageStateContext,
  useModalDialogContext,
  usePage,
} from 'client/hooks'

import {
  displaySnackBar,
  getCreateQuestionInitialValues,
  isAccountFull,
  getAccountStatus,
  createQuestionFormValuesToBody,
} from 'client/services'

import NewQuestionForm from './NewQuestionForm/new-question-form'
import QuestionPreview from './QuestionPreview/question-preview'
import { fixScrollPosition } from 'client/helpers'

export type View = 'FORM' | 'PREVIEW'

const CreateQuestionPage = () => {
  const { t } = useTranslation()

  const [activeView, setActiveView] = useState<View>('FORM')

  const { navigateTo } = usePage()
  const { setBrowseQuestionsPageState } = usePageStateContext()
  const { showQuestionModal, toggleNoAccessModal } = useModalDialogContext()
  const { authUser } = useAppContext()

  const accountStatus = getAccountStatus(authUser)

  const {
    data: createQuestionData,
    mutateAsync: createQuestion,
    isLoading: isCreatingQuestion,
  } = useCreateQuestion()

  const defaultValues = getCreateQuestionInitialValues(QuestionType.SingleChoice)

  const form = useForm<CreateQuestionFormValues>({
    mode: 'onChange',
    defaultValues,
  })

  const { reset, resetField, getValues, watch } = form

  useEffect(() => {
    const subscription = watch(({ type }, { name }) => {
      if (name === 'type') {
        resetField('correctOptions')
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  const handleCreateQuestion = async () => {
    if (isAccountFull(accountStatus)) {
      const values = getValues()
      const payload = createQuestionFormValuesToBody(values)
      return createQuestion(payload as IQuestion)
        .then(data => {
          setBrowseQuestionsPageState(state => ({
            ...state,
            filter: Filter.Created,
            category: 'any',
            search: '',
          }))
          navigateTo({ path: '/browse' })
          if (data?.msg) {
            displaySnackBar(data.msg)
          }
          setActiveView('FORM')
          fixScrollPosition(false)
          reset()
        })
        .catch(err => {
          if (err?.msg) {
            displaySnackBar(err.msg)
          }
          setActiveView('FORM')
          fixScrollPosition(false)
          reset()
        })
    } else {
      toggleNoAccessModal(true)
    }
  }

  return (
    <FormProvider {...form}>
      <AppSegment
        headerProps={{
          text:
            activeView === 'PREVIEW'
              ? t('createQuestionPage.headerPreview')
              : t('createQuestionPage.header'),
          iconProps: {
            name: 'WRITE',
          },
        }}
        maxWidth="medium"
        centered
      >
        {activeView === 'PREVIEW' ? (
          <QuestionPreview
            isCreatingQuestion={isCreatingQuestion}
            setActiveView={setActiveView}
            onSubmit={handleCreateQuestion}
          />
        ) : (
          <NewQuestionForm
            isCreatingQuestion={isCreatingQuestion}
            setActiveView={setActiveView}
          />
        )}
      </AppSegment>
      <QuestionModal
        question={createQuestionData?.question}
        open={showQuestionModal}
        loading={isCreatingQuestion}
      />
    </FormProvider>
  )
}

export default CreateQuestionPage
